// Admin > Company > Data (DESIGN.md "Data and account closure (console)", ADR 0062, story U1):
// the company's data exports and closing the account. Admins only.
import { type InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type Export, type ExportChanged, type ExportList, isExportRunning } from "@vd/api-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { qk } from "../../api/admin";
import { closureKeys, closurePreviewQuery, closureQuery, exportsQuery } from "../../api/closure";
import { liveSocket } from "../../api/ws";
import { requireAdmin, useMe } from "../../auth/useMe";
import { Crumbs } from "../../components/Crumbs";
import { CloseAccountCard } from "../../components/data/CloseAccountCard";
import { CloseAccountDialog, takeCloseResume } from "../../components/data/CloseAccountDialog";
import { ExportDialog } from "../../components/data/ExportDialog";
import { ExportsCard } from "../../components/data/ExportsCard";
import { KeepAccountDialog } from "../../components/data/KeepAccountDialog";
import { ErrorState, Loading } from "../../components/ui";
import { closeCardState, exportBlocked } from "../../lib/dataExports";

export const Route = createFileRoute("/admin/data")({
  staticData: { crumb: { label: "Data", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchInfiniteQuery(exportsQuery),
      context.queryClient.prefetchQuery(closureQuery),
    ]),
  component: DataPage,
});

/** How often the list refreshes while an export runs, besides its `export.changed` frames. */
const RUNNING_POLL_MS = 5000;

type Pages = InfiniteData<ExportList, string | null>;

/** `export.changed` moves a row's state and percent in place; a finished one reloads the list. */
function useExportFrames() {
  const qc = useQueryClient();
  useEffect(
    () =>
      liveSocket.onFrame((f) => {
        if (f.type === "export.changed") {
          const d = f.data as ExportChanged;
          qc.setQueryData<Pages>(closureKeys.exports, (old) =>
            old
              ? {
                  ...old,
                  pages: old.pages.map((p) => ({
                    ...p,
                    items: p.items.map((e) =>
                      e.id === d.id ? { ...e, state: d.state, percent: d.percent } : e,
                    ),
                  })),
                }
              : old,
          );
          if (!isExportRunning(d.state)) void qc.invalidateQueries({ queryKey: closureKeys.exports });
        } else if (f.type === "company.closure_changed") {
          void qc.invalidateQueries({ queryKey: closureKeys.closure });
          void qc.invalidateQueries({ queryKey: qk.me });
        }
      }),
    [qc],
  );
}

function DataPage() {
  const me = useMe();
  const exports = useInfiniteQuery({
    ...exportsQuery,
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.items.some((e) => isExportRunning(e.state)))
        ? RUNNING_POLL_MS
        : false,
  });
  const closure = useQuery(closureQuery);
  useExportFrames();

  const [exporting, setExporting] = useState(false);
  // Back from "Sign in again": the close dialog opens again, ready to continue.
  const [resumed] = useState(takeCloseResume);
  const [closing, setClosing] = useState(resumed);
  const [keeping, setKeeping] = useState(false);
  const exportRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const items = useMemo(() => {
    const seen = new Set<string>();
    const out: Export[] = [];
    for (const p of exports.data?.pages ?? []) {
      for (const e of p.items) {
        if (!seen.has(e.id)) {
          seen.add(e.id);
          out.push(e);
        }
      }
    }
    return out;
  }, [exports.data]);

  // A closure that cannot be read (an older server) leaves the Close account button.
  const c = closure.data ?? null;
  const card = closeCardState(c);
  // What closing now does, read when the dialog opens; the dialog waits for it.
  const preview = useQuery({ ...closurePreviewQuery, enabled: closing && card === "open" });

  const ready = !exports.isPending && !closure.isPending;
  return (
    <>
      <div className="flex max-w-[880px] flex-col gap-1 pb-5">
        <div className="-mt-1 mb-1 empty:hidden">
          <Crumbs />
        </div>
        <h1 className="font-display font-medium text-hero">Data</h1>
      </div>
      <div className="flex max-w-[880px] flex-col gap-5">
        {!ready ? (
          <Loading />
        ) : exports.isError ? (
          <ErrorState error={exports.error} onRetry={() => exports.refetch()} />
        ) : (
          <>
            <ExportsCard
              items={items}
              blocked={exportBlocked(items)}
              onExport={() => setExporting(true)}
              exportRef={exportRef}
              hasMore={exports.hasNextPage}
              loadingMore={exports.isFetchingNextPage}
              onLoadMore={() => void exports.fetchNextPage()}
            />
            {card === "hidden" ? null : (
              <CloseAccountCard
                companyName={me.tenant.name}
                closing={card === "closing" ? c : null}
                onClose={() => setClosing(true)}
                onKeep={() => setKeeping(true)}
                buttonRef={closeRef}
              />
            )}
          </>
        )}
      </div>
      <ExportDialog open={exporting} onClose={() => setExporting(false)} returnFocus={exportRef} />
      <CloseAccountDialog
        open={closing && ready && card === "open" && !preview.isPending}
        onClose={() => setClosing(false)}
        companyName={me.tenant.name}
        preview={preview.data ?? null}
        previewError={preview.error}
        resumed={resumed}
        returnFocus={closeRef}
      />
      {c && card === "closing" ? (
        <KeepAccountDialog
          closure={c}
          open={keeping}
          onClose={() => setKeeping(false)}
          returnFocus={closeRef}
        />
      ) : null}
    </>
  );
}
